import { ErrorHandler, LOCALE_ID, NgModule, inject, provideAppInitializer } from '@angular/core';
import {registerLocaleData} from '@angular/common';
import {ModeratRoutingModule} from "./moderat-routing.module";
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {MAT_DATE_LOCALE} from "@angular/material/core";
import {BrowserModule} from "@angular/platform-browser";
import {HttpClient, provideHttpClient, withInterceptorsFromDi} from "@angular/common/http";
import * as Sentry from "@sentry/angular";
import {Router} from "@angular/router";
import localeDE from "@angular/common/locales/de";
import {ModeratComponent} from "./moderat.component";
import {EinstiegComponent} from "./einstieg/einstieg.component";
import {MatCheckboxModule} from "@angular/material/checkbox";
import {VerbrauchsanalyseStartComponent} from "./verbrauchsanalyse/verbrauchsanalyse.start.component";
import {StepService} from "../services/step.service";
import {AdvisorSharedModule} from "../advisor.shared.module";
import {VerbrauchsanalyseStepComponent} from "./verbrauchsanalyse/verbrauchsanalyse.step.component";
import {BestandsaufnahmeStartComponent} from "./bestandsaufnahme/bestandsaufnahme.start.component";
import {BestandsaufnahmeStepComponent} from "./bestandsaufnahme/bestandsaufnahme.step.component";
import {VerbrauchsanalyseErgebnisComponent} from "./verbrauchsanalyse/verbrauchsanalyse.ergebnis.component";
import {SanierungComponent} from "./sanierung/sanierung.component";
import {MatExpansionModule} from "@angular/material/expansion";
import { SanierungMeasureComponent } from './sanierung/sanierung-measure.component';
import { SanierungInvestComponent } from './sanierung/sanierung-invest.component';
import {MatSliderModule} from "@angular/material/slider";
import {MatSlideToggleModule} from "@angular/material/slide-toggle";
import {SanierungIntroductionComponent} from "./sanierung/sanierung-introduction.component";
import {TranslateLoader, TranslateNoOpLoader, provideTranslateService} from "@ngx-translate/core";
import {PortalService} from "../services/portal.service";
import {VerbrauchsanalyseModeratStepVerbrauchsdatenComponent} from "./verbrauchsanalyse/verbrauchsanalyse-moderat.step.verbrauchsdaten.component";
import {AdvisorVerbrauchsanalyseModule} from "../advisor.verbrauchsanalyse.module";
import {VerbrauchsanalyseModeratAusstiegComponent} from "./verbrauchsanalyse/verbrauchsanalyse-moderat.ausstieg.component";
import {WpcheckStartComponent} from './wpcheck/wpcheck.start.component';
import {ExternalEntryComponent} from "./external-entry/external-entry.component";
import {AdvisorEnergyMapModule} from '../advisor.energy.map.module';
import {ModeratEnergyMapComponent} from './energy-map/moderat.energy.map.component';
import {ModeratEnergyMapDonatedComponent} from './energy-map/moderat.energy.map.donated.component';
import {SanierungHelpButtonComponent} from "./sanierung/sanierung.help-button.component";
import {SanierungHelpCalloutComponent} from "./sanierung/sanierung.help-callout.component";
import {SanierungSavingComponent} from './sanierung/sanierung-saving.coponent';
import { SanierungCostsOverviewComponent } from './sanierung/sanierung-costs-overview.component';
import { SanierungMeasuresComponent } from './sanierung/sanierung-measures.component';
import {SanierungFinancingComponent} from "./sanierung/sanierung-financing.component";
import {SanierungEconomyComponent} from "./sanierung/sanierung-economy.component";
import { SanierungForecastComponent } from './sanierung/sanierung-forecast.component';
import {HelptextSideComponent} from "../helptext/helptext.side.component";
import {BaseChartDirective} from "ng2-charts";
import {LineChartComponent} from "../chart/line-chart.component";
import {FernwaermeErgebnisComponent} from "./fernwaerme/fernwaerme.ergebnis.component";
import {FernwaermeTeaserComponent} from "./fernwaerme/fernwaerme.teaser.component";

// global definition of language prefixe
declare let advisorTranslationPrefix;

// AoT requires an exported function for factories
export function HttpLoaderFactory() {
  if(document?.location?.host === 'localhost:4201') {
    // workaround for sowing only the textkeys in the testing environment
    return new TranslateNoOpLoader();
  }
  const http = inject(HttpClient);
  return {
    getTranslation: (lang: string) => http.get<object>(
      (typeof advisorTranslationPrefix !== "undefined" ? advisorTranslationPrefix : "translations/" + PortalService.defaultPortal + "/moderat/") + lang + ".json"
    )
  };
}

// load the locales dynamic, based on the users browser language with fallback
const browserLocale = getBrowsersLocale();
switch(browserLocale.substring(0, 2)) {
  case 'de':
    registerLocaleData(localeDE, 'de');
    break;
}

/**
 * returns the users locale from the browser
 *
 * @returns {any}
 */
function getBrowsersLocale() {
  return 'de-DE';
}

@NgModule({
    declarations: [ModeratComponent, EinstiegComponent, VerbrauchsanalyseStartComponent,
      VerbrauchsanalyseModeratStepVerbrauchsdatenComponent,
        VerbrauchsanalyseStepComponent, VerbrauchsanalyseErgebnisComponent, VerbrauchsanalyseModeratAusstiegComponent,
        BestandsaufnahmeStartComponent, BestandsaufnahmeStepComponent, SanierungComponent, SanierungMeasureComponent,
        FernwaermeErgebnisComponent,
        FernwaermeTeaserComponent,
        SanierungInvestComponent,
        SanierungIntroductionComponent,
        WpcheckStartComponent,
        ExternalEntryComponent,
        ModeratEnergyMapComponent,
        ModeratEnergyMapDonatedComponent,
        SanierungHelpButtonComponent, SanierungHelpCalloutComponent,
        SanierungSavingComponent,
        SanierungCostsOverviewComponent,
        SanierungMeasuresComponent,
        SanierungFinancingComponent,
        SanierungEconomyComponent,
        SanierungForecastComponent,
        HelptextSideComponent,
        LineChartComponent
    ],
    bootstrap: [
        ModeratComponent
    ],
  imports: [
    BrowserModule,
        ModeratRoutingModule,
        ReactiveFormsModule,
        FormsModule,
        MatCheckboxModule,
        MatExpansionModule,
        MatSliderModule,
        MatSlideToggleModule,
        AdvisorSharedModule,
        AdvisorVerbrauchsanalyseModule,
        AdvisorEnergyMapModule,
        BaseChartDirective,
  ],
  providers: [
    {provide: MAT_DATE_LOCALE, useValue: 'de-DE'},
    {provide: LOCALE_ID, useValue: getBrowsersLocale()},
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
        dialogOptions: {
          user: {
            name: 'dummy',
            email: 'dummy@co2online.de'
          }
        }
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideAppInitializer(() => {
        const initializerFn = () => {
          inject(Sentry.TraceService)
        };
        return initializerFn();
      }),
    StepService,
    provideTranslateService({
      loader: {provide: TranslateLoader, useFactory: HttpLoaderFactory}
    }),
        provideHttpClient(withInterceptorsFromDi())
  ]
})
export class ModeratModule {}
